import { useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { isOpenablePage } from '@/router/pagePaths'
import { selectIsSuperAdmin, selectRoles, useAuthStore } from '@/stores/authStore'
import { useBreadcrumbLabelStore } from './breadcrumbLabels'
import { backLinkState } from './pageBack'

const ID_SEGMENT = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i
/** Path suffixes that have no page of their own, so they are shown but not linked. */
const NO_INDEX_SUFFIXES = ['/class', '/report-cards', '/returning', '/documents']
/** Plain-language names for path segments whose title case reads poorly. */
const SEGMENT_LABELS: Record<string, string> = {
  returning: 'Returning students',
  // Admin payments: plain names instead of the route segments.
  feed: 'All payments',
  customers: 'Families',
  'follow-up': 'Needs attention',
}

function titleCase(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment]
  return decodeURIComponent(segment)
    .split(/[-_ ]/)
    .map(word => (word ? word[0]?.toUpperCase() + word.slice(1) : word))
    .join(' ')
}

function singular(label: string): string {
  if (label === 'Children') return 'Child'
  if (label === 'Families') return 'Family'
  // "Class" / "Address" end in a double s and are already singular.
  return label.length > 3 && label.endsWith('s') && !label.endsWith('ss')
    ? label.slice(0, -1)
    : label
}

export type BreadcrumbItem = { label: string; to: string; linkable: boolean }

/** Trail for the current route, starting at Dashboard. Labels honour router state maps. */
export function useBreadcrumbTrail(): BreadcrumbItem[] {
  const location = useLocation()
  const registered = useBreadcrumbLabelStore(s => s.labels)
  const roles = useAuthStore(useShallow(selectRoles))
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin)
  const segments = location.pathname.split('/').filter(Boolean)
  const state =
    (location.state as {
      breadcrumbName?: string
      breadcrumbMap?: Record<string, string>
    } | null) ?? null

  const startIndex = segments[0] === 'dashboard' ? 1 : 0
  const dynamicCrumbs = segments.slice(startIndex).map((seg, idx, arr) => {
    const to = '/' + segments.slice(0, startIndex + idx + 1).join('/')
    // Raw identifiers are meaningless in a trail: call the page "<Thing> details".
    const defaultLabel = ID_SEGMENT.test(seg)
      ? `${singular(titleCase(segments[startIndex + idx - 1] ?? 'Record'))} details`
      : titleCase(seg)
    const mapLabel = registered[to] ?? state?.breadcrumbMap?.[to]
    const isLast = idx === arr.length - 1
    const label =
      mapLabel ?? (isLast && state?.breadcrumbName ? state.breadcrumbName : defaultLabel)
    // Only real pages this user can open: a URL prefix is not always a page (a 404), and
    // some parents are admin-only (Unauthorized for a teacher).
    const linkable =
      !NO_INDEX_SUFFIXES.some(suffix => to.endsWith(suffix)) &&
      isOpenablePage(to, { roles, isSuperAdmin })
    return { label, to, linkable }
  })

  return [{ label: 'Dashboard', to: '/dashboard', linkable: true }, ...dynamicCrumbs]
}

/**
 * Router state for links out of the current page, so the next page's "Back" returns
 * here (same tab and filters) under this page's own name.
 */
export function useBackToHereState() {
  const location = useLocation()
  const current = useBreadcrumbTrail().at(-1)
  return backLinkState({
    label: current?.label ?? 'Previous page',
    to: `${location.pathname}${location.search}`,
  })
}
